import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';

/**
 * StorageIntegrityService
 *
 * Detects client-side tampering of non-sensitive localStorage values by
 * maintaining a SHA-256 snapshot. On every startup, route change, and
 * (debounced) API call, it verifies that:
 *
 *   1. The stored snapshot (app_state) has not been modified  → hash check
 *   2. Each live localStorage value matches the stored snapshot → diff check
 *
 * If either check fails the application is frozen: localStorage is cleared
 * and the user is redirected to the login page.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * IMPORTANT CONSTRAINTS (by design):
 *  - Only non-sensitive, UI-level keys are included in the snapshot.
 *  - Tokens and refresh-tokens are NEVER included.
 *  - Backend remains the authoritative security layer.
 * ─────────────────────────────────────────────────────────────────────────
 */
@Injectable({ providedIn: 'root' })
export class StorageIntegrityService {
  private readonly router = inject(Router);
  private readonly authStateService = inject(AuthStateService);

  // ── localStorage key names ───────────────────────────────────────────────
  private readonly STATE_KEY = 'app_state';
  private readonly HASH_KEY  = 'app_state_hash';

  /**
   * Non-sensitive keys whose values are snapshotted and protected.
   * Any direct modification to these keys (without re-saving state) will be
   * detected as tampering.
   */
  readonly PROTECTED_KEYS: readonly string[] = [
    'BaseUrl',
    'currentCompany',
    'isLoggedIn',
    'isCustomerLogin',
    'Applications',
  ] as const;

  // ── Validation cache ─────────────────────────────────────────────────────
  /** Time of the last successful validation (used for debouncing). */
  private lastValidationTime = 0;
  private lastValidationResult = true;
  /**
   * How long (ms) to reuse the previous validation result when called from
   * the HTTP interceptor. Keeps overhead negligible across rapid API calls.
   */
  private readonly CACHE_TTL_MS = 10_000; // 10 seconds

  // ── Guard against re-entrant handler calls ───────────────────────────────
  private tamperingHandled = false;

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Snapshot all protected localStorage values and persist their SHA-256 hash.
   *
   * Call this after every successful write that touches a protected key:
   *   - After a successful login (all keys are populated)
   *   - After switching companies (BaseUrl / currentCompany update)
   */
  async saveState(): Promise<void> {
    const state   = this.collectLiveState();
    const json    = this.deterministicSerialize(state);
    const hash    = await this.sha256(json);

    localStorage.setItem(this.STATE_KEY, json);
    localStorage.setItem(this.HASH_KEY,  hash);

    // Bust validation cache so next check re-evaluates fresh values.
    this.lastValidationTime   = 0;
    this.lastValidationResult = true;
    this.tamperingHandled     = false;
  }

  /**
   * Validate the integrity of the current localStorage state.
   *
   * @param force - bypass the 10-second cache (use on startup and route guards)
   * @returns `true` when storage is clean, `false` when tampering is detected
   */
  async validateIntegrity(force = false): Promise<boolean> {
    const now = Date.now();

    if (!force && (now - this.lastValidationTime) < this.CACHE_TTL_MS) {
      return this.lastValidationResult;
    }

    const result = await this.performValidation();

    this.lastValidationTime   = now;
    this.lastValidationResult = result;

    return result;
  }

  /**
   * Convenience wrapper: validate and call handleTampering() if the check fails.
   *
   * @returns `true` when clean, `false` after triggering the tamper response
   */
  async checkIntegrity(force = false): Promise<boolean> {
    const valid = await this.validateIntegrity(force);
    if (!valid) {
      this.handleTampering();
    }
    return valid;
  }

  /**
   * Remove the integrity snapshot from localStorage and reset in-memory state.
   * Call this on logout (before or after localStorage.clear()).
   */
  clearState(): void {
    localStorage.removeItem(this.STATE_KEY);
    localStorage.removeItem(this.HASH_KEY);
    this.lastValidationTime   = 0;
    this.lastValidationResult = true;
    this.tamperingHandled     = false;
  }

  /**
   * Respond to detected tampering:
   *   1. Fire a signout API call (fire-and-forget, bypasses Angular interceptors)
   *   2. Wipe localStorage
   *   3. Redirect to login
   *
   * Idempotent – safe to call multiple times.
   */
  handleTampering(): void {
    if (this.tamperingHandled) return;
    this.tamperingHandled = true;

    console.error('[StorageIntegrity] Tampering detected – signing out and clearing session.');

    // ── Read session data BEFORE wiping localStorage ─────────────────────────
    const tokenCompanies = Object.keys(this.authStateService.getCompanyTokens());
    const activeCompany = this.authStateService.currentCompany()
      || localStorage.getItem('currentCompany')
      || tokenCompanies[0]
      || 'unity';
    const token = this.authStateService.getCompanyToken(activeCompany)
      || this.authStateService.token()
      || '';
    let userName = this.authStateService.getUserName();
    try {
      const raw = localStorage.getItem(`tibss.${activeCompany}-userDetails`);
      if (raw && !userName) {
        const parsedCompanyUser = JSON.parse(raw) as { userName?: string; name?: string };
        userName = parsedCompanyUser.userName || parsedCompanyUser.name || '';
      }
      if (!userName) {
        const globalRaw = localStorage.getItem('userDetails');
        if (globalRaw) {
          const parsedGlobalUser = JSON.parse(globalRaw) as { userName?: string; name?: string };
          userName = parsedGlobalUser.userName || parsedGlobalUser.name || '';
        }
      }
    } catch { /* ignore parse errors */ }

    // ── Fire signout (fire-and-forget) ───────────────────────────────────────
    // Use native fetch() so the call:
    //   - bypasses Angular HTTP interceptors (avoids re-triggering integrity check)
    //   - survives Angular route transitions (keepalive: true)
    fetch(`${environment.CAS_BASE_RUL}api/CAS/signout`, {
      method:    'POST',
      headers:   {
        'Content-Type': 'application/json',
        'X-Client-Code': 'CRM'
      },
      credentials: 'include',
      body:      JSON.stringify({ userName, tokens: { [activeCompany]: token } }),
      keepalive: true, // request completes even when navigating away
    }).catch(() => { /* signout failure must never block the freeze */ });

    // ── Wipe everything so no stale/tampered data persists ───────────────────
    localStorage.clear();

    // ── Navigate to login ─────────────────────────────────────────────────────
    // Use Angular Router when available; fall back to hard redirect during
    // early lifecycle phases (e.g. APP_INITIALIZER).
    try {
      this.router.navigate(['/auth/login'], { queryParams: { reason: 'security' } });
    } catch {
      window.location.href = '/auth/login?reason=security';
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /** Collect current values of every protected key from live localStorage. */
  private collectLiveState(): Record<string, string | null> {
    return Object.fromEntries(
      this.PROTECTED_KEYS.map(k => [k, localStorage.getItem(k)])
    );
  }

  /**
   * Serialize a state object to JSON with keys always in alphabetical order,
   * ensuring the same object always produces the same string.
   */
  private deterministicSerialize(state: Record<string, string | null>): string {
    const sorted: Record<string, string | null> = {};
    for (const key of Object.keys(state).sort()) {
      sorted[key] = state[key];
    }
    return JSON.stringify(sorted);
  }

  /** SHA-256 via the native Web Crypto API (available in all modern browsers). */
  private async sha256(data: string): Promise<string> {
    const encoded     = new TextEncoder().encode(data);
    const hashBuffer  = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /** Core two-step validation logic executed when the cache is stale. */
  private async performValidation(): Promise<boolean> {
    const storedStateJson = localStorage.getItem(this.STATE_KEY);
    const storedHash      = localStorage.getItem(this.HASH_KEY);

    // No snapshot stored → fresh session or post-logout; nothing to verify.
    if (!storedStateJson || !storedHash) {
      return true;
    }

    // ── Guard 1: ensure app_state itself has not been modified ───────────────
    const recomputedHash = await this.sha256(storedStateJson);
    if (recomputedHash !== storedHash) {
      console.warn('[StorageIntegrity] Hash mismatch – app_state snapshot has been tampered with.');
      return false;
    }

    // ── Guard 2: ensure live localStorage values match the snapshot ──────────
    let storedState: Record<string, string | null>;
    try {
      storedState = JSON.parse(storedStateJson);
    } catch {
      console.warn('[StorageIntegrity] Failed to parse stored state – treating as tampered.');
      return false;
    }

    const liveState = this.collectLiveState();

    // ── Collect ALL mismatched keys first (don't bail on the first one) ──────
    const mismatches: string[] = [];
    for (const key of this.PROTECTED_KEYS) {
      if (liveState[key] !== (storedState[key] ?? null)) {
        mismatches.push(key);
      }
    }

    // No mismatches → clean.
    if (mismatches.length === 0) {
      return true;
    }

    // ── Check if this looks like a legitimate logout / localStorage.clear() ──
    // When localStorage is cleared (by another tab, inactivity, or a logout
    // that raced with an in-flight request), ALL protected keys become null.
    // Detecting this as "tampering" causes false logouts. Instead, recognise
    // the pattern and clean up gracefully.
    const isLoggedInLive         = liveState['isLoggedIn'];
    const isLoggedInSnapshot     = storedState['isLoggedIn'] ?? null;
    const isCustomerLoginLive    = liveState['isCustomerLogin'];
    const isCustomerLoginSnapshot = storedState['isCustomerLogin'] ?? null;

    // Case 1: isLoggedIn was 'true' and is now 'false' or null → legitimate logout.
    const loggedOutNormally =
      isLoggedInSnapshot === 'true' &&
      (isLoggedInLive === 'false' || isLoggedInLive === null);

    // Case 2: isCustomerLogin was 'true' and is now 'false' or null → legitimate logout.
    const customerLoggedOut =
      isCustomerLoginSnapshot === 'true' &&
      (isCustomerLoginLive === 'false' || isCustomerLoginLive === null);

    // Case 3: ALL protected keys are now null → localStorage was fully cleared.
    const allKeysNull = this.PROTECTED_KEYS.every(k => liveState[k] === null);

    if (loggedOutNormally || customerLoggedOut || allKeysNull) {
      console.info(
        '[StorageIntegrity] Logged-out / cleared state detected – clearing old snapshot.',
        { mismatches, loggedOutNormally, customerLoggedOut, allKeysNull }
      );
      this.clearState();
      return true;
    }

    // ── Genuine mismatch → log details for debugging ────────────────────────
    for (const key of mismatches) {
      console.warn(`[StorageIntegrity] Key "${key}" differs from snapshot.`, {
        snapshot: storedState[key],
        live:     liveState[key],
      });
    }

    return false;
  }
}
